// Mock medical necessity review, the way a UM nurse checks a precert request against criteria
// (InterQual or MCG in real life). Pure functions, so this can move to the backend with the claims engine.
import { ICD_CODES } from "./emrData.js";

export const normalizeIcd = (code) => {
    const text = String(code || "").trim().toUpperCase().replace(/\s+/g, "");
    if (!text || text.includes(".") || text.length <= 3) return text;
    return `${text.slice(0, 3)}.${text.slice(3)}`;
};

export const icdLabel = (code) => {
    const found = ICD_CODES.find((item) => item.code === normalizeIcd(code));
    return found ? `${found.code} (${found.description})` : normalizeIcd(code);
};

// A criteria set for the exact procedure code wins over one for the whole service.
export const findCriteria = (sets = [], serviceKey, code) => {
    const procedure = String(code || "").trim().toUpperCase();
    return sets.find((set) => procedure && (set.codes || []).includes(procedure))
        || sets.find((set) => set.serviceKey === serviceKey && !(set.codes || []).length)
        || null;
};

export const diagnosisSupports = (set, diagnosisCodes = []) => (
    !(set?.diagnoses || []).length || diagnosisCodes.map(normalizeIcd).some((code) => set.diagnoses.includes(code))
);

const addDays = (date, days) => {
    const next = new Date(`${date}T00:00:00Z`);
    next.setUTCDate(next.getUTCDate() + Number(days || 0));
    return next.toISOString().slice(0, 10);
};

// request: { serviceKey, code, diagnosisCodes, criteriaMet: [criterion index], units, from }
export function reviewRequest({ request, sets }) {
    const set = findCriteria(sets, request.serviceKey, request.code);
    const units = Math.max(1, Number(request.units || 1));
    const from = request.from || new Date().toISOString().slice(0, 10);
    if (!set) {
        return {
            status: "approved",
            criteriaId: "",
            units,
            from,
            to: addDays(from, 90),
            checks: [],
            reason: "No medical necessity criteria cover this service, so it is approved as requested.",
        };
    }
    const diagnosisOk = diagnosisSupports(set, request.diagnosisCodes);
    const criteriaChecks = set.criteria.map((label, index) => ({ label, met: (request.criteriaMet || []).includes(index) }));
    const metCount = criteriaChecks.filter((check) => check.met).length;
    const criteriaOk = !set.criteria.length || (set.mode === "all" ? metCount === set.criteria.length : metCount > 0);
    const checks = [
        ...(set.diagnoses.length ? [{ label: `A supporting diagnosis: ${set.diagnoses.map(icdLabel).join(", ")}`, met: diagnosisOk }] : []),
        ...criteriaChecks,
    ];
    if (diagnosisOk && criteriaOk) {
        const approved = Math.min(units, Number(set.maxUnits || units));
        return {
            status: "approved",
            criteriaId: set.id,
            units: approved,
            from,
            to: addDays(from, set.validDays || 90),
            checks,
            reason: `Meets ${set.name}.${approved < units ? ` Approved ${approved} of the ${units} units asked for. More need another review.` : ""}`,
        };
    }
    const why = !diagnosisOk
        ? "the diagnosis doesn't support it"
        : set.mode === "all"
            ? `${set.criteria.length - metCount} of ${set.criteria.length} criteria ${set.criteria.length - metCount === 1 ? "isn't" : "aren't"} met`
            : "none of the criteria are met";
    return {
        status: set.notMet === "deny" ? "denied" : "pended",
        criteriaId: set.id,
        units: 0,
        from,
        to: addDays(from, set.validDays || 90),
        checks,
        reason: `Doesn't meet ${set.name}: ${why}.${set.notMet === "deny" ? "" : " Sent to a nurse or doctor for review."}`,
    };
}
